import { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import CustomSelect from "../custom-select/custom-select";
import { customStyles } from "../custom-select/custom-styles";
import { useGetBanksQuery } from "../../store/selectableSlice";
import { useValidateBankAccountMutation } from "../../store/admin/settings/company-bank";
import { useCreateBankInfoMutation } from "../../store/admin/settings/societyBankSlice";
import { Input } from "../Ui/new-input";

const CreateBankInfo = ({ setModalPage }) => {
  const [checked, setChecked] = useState(false);

  const {
    data: banks = [],
    isLoading: isLoadingBanks,
    // isError: isErrorBanks,
    // error: errorLoadingBanks,
  } = useGetBanksQuery();

  const [validateAccount, { isLoading: isValidating }] =
    useValidateBankAccountMutation();

  const stringifyFormikValues = (values) => {
    const stringifiedValues = {};
    Object.keys(values).forEach((key) => {
      stringifiedValues[key] = String(values[key]);
    });
    return stringifiedValues;
  };

  const formik = useFormik({
    initialValues: {
      bank_name: "",
      account_name: "",
      account_number: "",
    },
    onSubmit: (values) => {
      const stringifiedValues = stringifyFormikValues(values);
      handleSubmit(stringifiedValues);
    },
    validationSchema: Yup.object({
      bank_name: Yup.string().required("Bank name is required"),
      account_name: Yup.string().required("Account name is required"),
      account_number: Yup.string()
        .required("Account Number is required")
        .matches(/^\d{10}$/, "Account number must be exactly 10 digits"),
    }),
  });

  const handleAccountNumberChange = (e) => {
    // Keep as string digits only — type="number" drops leading zeros (e.g. 0127396509).
    const digitsOnly = String(e.target.value ?? "")
      .replace(/\D/g, "")
      .slice(0, 10);
    formik.setFieldValue("account_number", digitsOnly);
    if (digitsOnly.length !== 10) {
      formik.setFieldValue("account_name", "");
    }
  };

  async function handleValidation(value) {
    try {
      const response = await validateAccount(value).unwrap();
      
      // After transformResponse, response is the data object directly: { account_number, account_name, bank_id }
      if (response?.account_name) {
        toast.success("Account validated successfully");
        formik.setValues({
          ...formik.values,
          account_name: response.account_name,
        });
      } else {
        toast.error("Could not validate account. Please check the account number and bank.");
        formik.setValues({
          ...formik.values,
          account_name: "",
        });
      }
    } catch (error) {
      const errorMessage = error?.data?.message || "Failed to validate account. Please check the account number and bank.";
      toast.error(errorMessage);
      formik.setValues({
        ...formik.values,
        account_name: "",
      });
    }
  }

  useEffect(() => {
    const value = String(formik.values.account_number ?? "");

    if (/^\d{10}$/.test(value) && formik.values.bank_code) {
      handleValidation({
        account_number: value,
        bank_code: formik.values.bank_code,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.bank_name, formik.values.account_number]);

  const [creatingBankInfo, { isLoading: isCreatingBankInfo }] =
    useCreateBankInfoMutation();

  async function handleSubmit(values) {
    const response = await creatingBankInfo(values);

    if ("error" in response) {
      const { message } = response.error.data;
      return toast.error(message);
    }
    const { message, status } = response?.data;
    if (status) {
      toast.success(message);
      setModalPage?.();
    }
    if (!status) {
      toast.error(message);
    }
  }
  return (
    <form className="w-full flex flex-col" onSubmit={formik.handleSubmit}>
      <div className="my-2">
        <CustomSelect
          options={[
            ...banks.map((bank) => ({
              value: bank.code,
              label: bank.title,
            })),
          ]}
          styles={customStyles}
          label={"Branch"}
          isLoading={isLoadingBanks}
          onChange={(value) => {
            formik.setFieldValue("bank_code", value.value);
            formik.setFieldValue("bank_name", value.label);
          }}
          placeholder="Select contribution branch"
        />
        {formik?.touched.bank_name && formik.errors.bank_name && (
          <span className="text-sm text-red-500 font-circular_book block w-full">
            {formik.errors.bank_name}
          </span>
        )}
      </div>
      <Input
        type="text"
        label="Account number"
        placeholder={"Input account number"}
        name="account_number"
        onChange={handleAccountNumberChange}
        onBlur={formik.handleBlur}
        value={formik.values.account_number}
        error={formik?.touched.account_number && formik.errors.account_number}
        errorMessage={formik.errors.account_number}
      />
      <Input
        label="Account name"
        placeholder={"Ifesowapo Cooperatives"}
        name="account_name"
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values.account_name}
        error={formik?.touched.account_name && formik.errors.account_name}
        errorMessage={formik.errors.account_name}
        readOnly
        loading={isValidating}
      />

      <div className="flex gap-2 items-center w-full">
        <input
          type="checkbox"
          name=""
          id=""
          className="accent-new-primary h-6 w-6"
          onChange={() => setChecked(!checked)}
        />
        <span className="text-sm text-[#858585]">
          Check the box to confirm bank information and cooperative information
          are the same
        </span>
      </div>
      <div className="w-full my-4 flex justify-center">
        <button
          className="w-[200px] h-[50px] rounded-[10px] bg-new-primary text-white font-semibold disabled:opacity-60"
          disabled={!checked}
        >
          {isCreatingBankInfo ? "Submitting bank info..." : "Submit bank info"}
        </button>
      </div>
    </form>
  );
};

export default CreateBankInfo;
